import { useContext, useEffect, useState } from "react";
// next
// @mui
import {
  Avatar,
  Box,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
// components
import { AuthContext } from "@/contexts/JWTContext";
import useEditorService from "@/hooks/useEditorService";
import { ICONS } from "@/modules/ui-elements/Icon";
import { resetTemplateReducer } from "@/redux/reducers/template.reducer";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import useAuth from "src/hooks/useAuth";
import useIsMountedRef from "src/hooks/useIsMountedRef";
import { EventTrackName } from "src/modules/segment-event-tracking/constant";
import { TrackWebappEvent, resetSegmentClient } from "src/modules/segment-event-tracking/segmentEventTracking";
import { reset } from "src/redux/reducers/file-upload.reducer";
import { resetJobsReducer, setUserTrackInfoPayload } from "src/redux/reducers/jobs.reducer";
import { resetModifyImageReducer } from "src/redux/reducers/modifyImage.reducer";
import { resetMyProjectReducer } from "src/redux/reducers/myProject.reducer";
import { resetSettingReducer } from "src/redux/reducers/settings.reducer";
import { dispatch, useSelector } from "src/redux/store";
import { getUserNameInitials } from "src/utils/helpers";
import MenuPopover from "../../../components/MenuPopover";
import { IconButtonAnimate } from "../../../components/animate";
import ErrorBoundary from "@/modules/canvas/errorBoundry";
// ----------------------------------------------------------------------


export default function AccountPopover() {
  const [open, setOpen] = useState(null);
  const { user, logout } = useAuth();
  const router = useRouter();
  const ctx = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const {userTrackInfoPayload}=useSelector((state)=>state.jobs);
  const [profileImg, setProfileImg] = useState("");
  const {updateUser} = useSelector(state=>state.setting);
  const {destroy} = useEditorService();
  const {entireStrapiData} = useSelector((state)=> state.strapi); 
  const strapiData = entireStrapiData?.AccountSetting2Web5?.AccountSetting2Web5?.NewProfileDropDown;
  const MENU_OPTIONS = [
    {
      label: strapiData?.[0]?.Heading || "Home",
      linkTo: "/dashboard/homePage",
      icon:ICONS.homeDark,
      track:`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_home_menu}`
    },
    {
      label: strapiData?.[1]?.Heading ||"Profile",
      linkTo: "/dashboard/setting?tab=PROFILE",
      icon:ICONS.accountDark,
      track:`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_profile_dropdwn_profile}`
    },
    {
      label: strapiData?.[2]?.Heading ||"My Projects",
      linkTo: "/dashboard/my_projects?tab=COMPLETED",
      icon:ICONS.myWorkDark,
      track:`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_profile_dropdwn_profile}`
    },
    {
      label: strapiData?.[3]?.Heading ||"Settings",
      linkTo: "/dashboard/setting?tab=ACCOUNT_SETTING",
      icon:ICONS.settingDark,
      track:`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_setting_dropdwn_profile}`
    },
  ];
  const handleOpen = (event) => {
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_profile_icon_header}`,userTrackInfoPayload);
    setOpen(event.currentTarget);
  };

  const handleClose = (track) => {
    if(track){
    TrackWebappEvent(track,userTrackInfoPayload);
    }
    setOpen(null);
  };

  const redirectHandler = ({linkTo,track}) => {
    destroy();
    router.push(linkTo);
    handleClose(track);
  }
  
  const isMountedRef = useIsMountedRef();

  const handleLogout = async () => {
    TrackWebappEvent(`${process.env.NEXT_PUBLIC_EVENT_TRACK_TYPE}${EventTrackName._click_log_out_dropdwn_profile}`,userTrackInfoPayload);
    try {
      ctx.isSocialLoginHandler(true);
      // dispatch(setReferredEmailId(''));
      // dispatch(setReferredPhoneNo(''));
      // dispatch(setReferredCountryCode(''));
      destroy();
      router.push('/dashboard/');
      await logout();
      // router.push("/dashboard/editor");
      queryClient.invalidateQueries();
      sessionStorage.removeItem('authFailed');
      sessionStorage.removeItem('query_info');
      dispatch(reset());
      dispatch(setUserTrackInfoPayload({}));
      dispatch(resetMyProjectReducer());
      dispatch(resetSettingReducer());
      dispatch(resetModifyImageReducer());
      dispatch(resetJobsReducer());
      dispatch(resetTemplateReducer());
      if (isMountedRef.current) {
        handleClose();
      }
      resetSegmentClient();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }finally{
      ctx.isSocialLoginHandler(false);
    }
  };

  useEffect(()=>{
    if(user?.user?.profile_image){
      setProfileImg(user?.user?.profile_image)
    }
  },[user?.user?.profile_image]);
  
  useEffect(()=>{
    if(updateUser?.data?.profileImage){
      setProfileImg(updateUser?.data?.profileImage);
    }
  },[updateUser]);
  
  return <>
    <ErrorBoundary name={'Loginopen'}>
    <IconButtonAnimate
      onClick={handleOpen}
      sx={{
        p: 0,
        ...(open && {
          "&:before": {
            zIndex: 1,
            content: "''",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            position: "absolute",
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            border: "1px solid #ffffff50",
            padding: "6px",
          },
        }),
      }}
    >
      <ErrorBoundary name={'LoginAnimate'}>
    {profileImg ? (
      <Avatar
        src={
          profileImg
        }
        alt={getUserNameInitials(user?.user?.name)}
        sx={{border: "1px solid #ffffff50", padding: "6px",}}
      >
      </Avatar>
    ) : user?.user?.name? (
      <Avatar
         sx={{border: "1px solid #ffffff50", padding: "6px",}}
      >
        {user?.user?.name? getUserNameInitials(user?.user?.name):''}
      </Avatar>
    ) : 
    <Avatar
    sx={{
      padding: "6px",
      "& .MuiAvatar-img": { borderRadius: "50%" },
      border: "1px solid #ffffff50",
    }}
      src={
        "https://assets.dresma.com/DoMyShoot/App%20Screens/231.png"
      }
      alt={'profileImg'}
    >
    </Avatar>
    }
    </ErrorBoundary>
    </IconButtonAnimate>

    <MenuPopover
      open={Boolean(open)}
      anchorEl={open}
      onClose={()=>handleClose()}
      sx={{
        p: 0,
        mt: 1.5,
        ml: 0.75,
        width:'300px',
        "& .MuiMenuItem-root": {
          typography: "body2",
          borderRadius: 0.75,
        },
      }}
    >
      <Box sx={{ my: '10px', px: 2.5 }}>
        {/* <Typography variant="subtitle2" noWrap>
          {user?.user?.name}
        </Typography> */}
        <Typography variant="subtitle2" fontSize={16} py={'9px'} noWrap>
          {user?.user?.email}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: "dashed" }} />

      <Stack>
        {MENU_OPTIONS.map((option) => (
            <MenuItem key={option.label} sx={{p:'10px 14px',m:'10px'}} onClick={()=>redirectHandler(option)}>
              <img src={option?.icon} style={{width:'16px',height:'16px',marginRight:'8px'}}/> 
              <span>{option.label}</span>
            </MenuItem>
        ))}
      </Stack>

      <Divider sx={{ borderStyle: "dashed" }} />

      <MenuItem onClick={handleLogout} sx={{p:'10px 14px',m:'10px'}}>
        {strapiData?.[4]?.Heading ||`Logout`}
      </MenuItem>
    </MenuPopover>
    </ErrorBoundary>
  </>;
}
